import { api, requireAccount, withAccount, type DisclosureLevel } from "./core";

// Typed API methods
export interface MemberSignal {
  id: number;
  author: string;
  github_user_id?: number;
  production: number;
  catalysis: number;
  survival: number;
  raw_survival: number;
  robust_survival: number;
  dormant_survival: number;
  design: number;
  breadth: number;
  debt_cleanup: number;
  indispensability: number;
  gravity: number;
  // The 通期/all-time twin of `gravity`: the durable structural footprint
  // (raw-survival gate, indispensability-leaned shape). `gravity` reads the
  // gravity that is live right now (robust-survival gate) and goes quiet for a
  // settled foundation; lifetime_gravity reads what lasting structure was left.
  // Shown as the headline in 通期 mode. 0 for redacted members (D-05) and for
  // observations taken before lifetime_gravity existed — fall back to `gravity`.
  lifetime_gravity?: number;
  // Real gravity that survives D-05 redaction (while `gravity` above is zeroed
  // for score-redacted members). Read it ONLY in the 世代交代 views — the
  // GravityHeatmap and the Logbook generational table — which always show the
  // true gravity history (bus-factor / collapse archaeology). Everywhere else
  // (radar, orbit line, Gravity Signals NUMBER) respects disclosure via
  // `gravity` / the disclosure level. The Gravity Signals view sizes its bars
  // with it but does NOT rank people on it (neutral name order).
  gravity_handover?: number;
  total: number;
  total_commits: number;
  lines_added: number;
  lines_deleted: number;
  recently_active: boolean;
  role_archetype: string;
  style_archetype: string;
  state_archetype: string;
  // D-05 disclosure level the BE applied to THIS row for the current
  // viewer. "full" → real name + numbers; "context" → real name +
  // archetype, numbers zeroed; "observation" → "Member-N", numbers
  // zeroed, but still an ACTIVE (recently_active) member. The FE labels
  // the row by this instead of mistaking a hidden 0.0 for a real score.
  disclosure: DisclosureLevel;
  // is_core — structural "load-bearing member" flag (recently active + at/above
  // the senior Impact bar), computed by the BE on the RAW score before redaction.
  // Carries the QUALITATIVE fact that this person holds gravity WITHOUT any
  // number, so a non-disclosed (context) member can be marked "Core" while every
  // score still reads 非開示. Always false on an anonymised (observation) row —
  // we never reveal that an anonymous member is core. Optional for legacy/older
  // BEs that omit it (treated as false).
  is_core?: boolean;
  // Trust Signal — credibility of Impact Signal
  trust_signal: number;
  trust_repo_publicity: number;
  trust_contributor_diversity: number;
  trust_cross_repo_validation: number;
  trust_temporal_consistency: number;
  trust_organic_pattern: number;
  trust_flags: string[] | null;
}

export interface Snapshot {
  id: number;
  account_id: number;
  repository_id: number;
  domain: string;
  period_start: string;
  period_end: string;
  status: string;
  // git_sha is the D-02 anchor of the observed period. The galaxy-view
  // adapter used to drop it when synthesising this legacy stub; it's
  // preserved (optional) so the Logbook can tie observation_notes to a
  // period by sha. Absent on Snapshots that come straight from the BE
  // trigger endpoints.
  git_sha?: string;
}

export interface SnapshotWithSignals {
  snapshot: Snapshot;
  signals: MemberSignal[];
  repo_name?: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: number;
    login: string;
    avatar_url: string;
  };
}

// snapshotApi exposes the trigger + worker status surface only. The
// legacy `latest` / `get` reads were removed — every read goes through
// galaxyApi.* below so the dedup, canonical merge, and disclosure
// filter pipeline is applied once on the BE.
export const snapshotApi = {
  trigger: (data: { repo_id: number; clone_url: string; account_login: string; repo_name: string }) =>
    api.post<Snapshot>("/snapshots/trigger", data),
  triggerAccount: (accountLogin: string) =>
    api.post<{ status: string }>("/snapshots/trigger-account", { account_login: requireAccount(accountLogin, "snapshotApi.triggerAccount") }),
  progress: (accountLogin: string) =>
    api.get<{ phase: string; step?: number; total?: number; detail?: string; started_at?: string; error?: string }>(`/snapshots/progress?account_login=${encodeURIComponent(requireAccount(accountLogin, "snapshotApi.progress"))}`),
  // workerStatus is a global heartbeat, not galaxy-scoped — keep it without
  // an account param. The `jobs` array carries account_login per row so the
  // sidebar can render which galaxy each in-flight job belongs to.
  workerStatus: () => api.get<{ alive: boolean; last_seen: number; queue_size: number; jobs?: { type: string; account_login: string }[] }>("/worker/status"),
  observationLogRecent: (accountLogin: string, limit = 20) =>
    api.get<ObservationLogEntry[]>(withAccount("/observation-logs", requireAccount(accountLogin, "snapshotApi.observationLogRecent"), { limit: String(limit) })),
  observationFailuresRecent: (accountLogin: string, limit = 50) =>
    api.get<ObservationFailure[]>(withAccount("/observation-failures", requireAccount(accountLogin, "snapshotApi.observationFailuresRecent"), { limit: String(limit) })),
};

// ObservationQuota is the per-day manual-observation cap state for a galaxy
// (rolling 24h). `used`/`manual_cap` drive the "today used/cap" line (manual_cap
// is cap minus any slot reserved for the auto-observation schedule); `cap` is the
// total; `resets_at` (RFC3339) is the next-unlock instant when capped, null when a
// slot is free. `scheduled`/`reserved` say whether a daily auto-run holds a slot.
export interface ObservationQuota {
  plan: string;
  used: number;
  cap: number;
  manual_cap: number;
  reserved: number;
  scheduled: boolean;
  resets_at: string | null;
}

export const observationQuotaApi = {
  get: (accountLogin: string) =>
    api.get<ObservationQuota>(withAccount("/observation-quota", requireAccount(accountLogin, "observationQuotaApi.get"))),
};

// ObservationSchedule is a galaxy's daily auto-observation config. `enabled` arms
// the hourly scheduler; `hour` (0-23, local) + `timezone` (IANA) say when it runs.
// `eligible` is whether the galaxy's plan may arm it (Pro/Nova); the FE disables
// the toggle + shows an upgrade CTA when false.
export interface ObservationSchedule {
  enabled: boolean;
  hour: number;
  timezone: string;
  eligible: boolean;
  plan: string;
}

export const observationScheduleApi = {
  get: (accountLogin: string) =>
    api.get<ObservationSchedule>(
      withAccount("/observation-schedule", requireAccount(accountLogin, "observationScheduleApi.get")),
    ),
  // Save returns the fresh status (same shape as get) so the caller can update
  // without a second round-trip.
  save: (accountLogin: string, body: { enabled: boolean; hour: number; timezone: string }) =>
    api.put<ObservationSchedule>(
      withAccount("/observation-schedule", requireAccount(accountLogin, "observationScheduleApi.save")),
      body,
    ),
};

// Append-only per-sha observation log (D-02). Distinct from the legacy
// observation_logs (plural) job-run history which the new schema dropped.
export interface ObservationLogEntry {
  id: number;
  galaxy_id: number;
  git_sha: string;
  observation_kind: string;
  observed_at: string; // RFC3339
  worker_version: string;
  duration_ms: number | null;
}

// states_observation_failures row (D-01 state-class). Resolved failures carry
// the observed_at of the successful run that cleared them — the Logbook panel
// renders that as "✓ resolved by the YYYY/MM/DD observation".
export interface ObservationFailure {
  id: number;
  galaxy_id: number;
  git_sha?: string;
  observation_kind?: string;
  repo_full_name?: string;
  failed_stage: string; // clone | signal | persist | trust | health | other
  error_message: string;
  worker_version: string;
  attempted_at: string; // RFC3339
  // heartbeat_at is bumped ~every 60s while a "started" lifecycle marker's job
  // is alive. Present only on job-start markers. A fresh heartbeat means the job
  // is still running (render 実行中, not 失敗); a stale one means an orphaned /
  // OOM-killed worker.
  heartbeat_at?: string; // RFC3339
  resolved_at?: string;
  resolved_by_observed_at?: string;
}
